import { useMemo } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import Joi from 'joi'
import { useForm } from 'react-hook-form'

import {
  ControlledSelector,
  ControlledTextField,
  FplDialogTitle,
  ISelectorOptions,
  LoadingBackdrop,
} from 'components/common'
import {
  CreateBidAllocationDtoInput,
  useGetDriverByCurrentUserQuery,
  useGetMyAvailableSubDriversQuery,
  useGetOwnersVehiclesQuery,
} from 'generated/graphql'
import { useConfirmDialog } from 'providers'

interface ISupplierAllocateDialogProps {
  openDialog: boolean
  onCloseDialog: () => void
  onAllocateDriver: (formValue: CreateBidAllocationDtoInput) => void
  loading: boolean
  bookingId: number
}

const labelId = 'add-driver-allocation-dialog-title'

export const SupplierAllocateDialog = (props: ISupplierAllocateDialogProps) => {
  const { openDialog, onCloseDialog, bookingId, onAllocateDriver, loading } = props

  const allocationSchema = Joi.object({
    driverId: Joi.string().required().messages({ 'any.required': 'Driver is required' }),
    bookingId: Joi.string().required(),
    vehicleId: Joi.string().required().messages({ 'any.required': 'Vehicle is required' }),
  })

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<CreateBidAllocationDtoInput>({
    shouldUnregister: true,
    defaultValues: {
      driverId: '',
      bookingId: bookingId.toString(),
      vehicleId: '',
    },
    resolver: joiResolver(allocationSchema),
  })

  const { data: driverData } = useGetDriverByCurrentUserQuery()

  const { loading: availableDriversLoading, data: availableDriversData } =
    useGetMyAvailableSubDriversQuery({
      variables: {
        bookingId: bookingId,
      },
      fetchPolicy: 'no-cache',
    })

  const { loading: vehiclesLoading, data: vehiclesData } = useGetOwnersVehiclesQuery({
    onCompleted: (response) => {
      const defaultVehicleId = response.ownersVehicles.filter(
        (v) => v.id === driverData?.driverByCurrentUser?.activeVehicleId,
      )[0]?.id
      if (defaultVehicleId) {
        setValue('vehicleId', defaultVehicleId)
      }
    },
  })

  const availableDrivers: ISelectorOptions = useMemo(() => {
    if (availableDriversData?.availableSubDrivers) {
      return availableDriversData.availableSubDrivers.map((driver) => ({
        id: driver.id,
        label: driver.name,
        value: driver.id,
      }))
    }

    return []
  }, [availableDriversData])

  const vehicles: ISelectorOptions = useMemo(() => {
    if (vehiclesData?.ownersVehicles) {
      return vehiclesData.ownersVehicles.map((vehicle) => ({
        id: vehicle.id,
        label: `${vehicle.vehicleType.name} - ${vehicle.registrationNumber} `,
        value: vehicle.id,
      }))
    }

    return []
  }, [vehiclesData])

  const handleApprove = () => {
    const values = getValues()
    onAllocateDriver(values)
  }

  const { confirmOpen } = useConfirmDialog({
    onConfirmApprove: handleApprove,
  })

  const onSubmitSuccess = () => {
    confirmOpen({
      title: 'Are you sure you want to allocate this driver?',
      content: 'This action cannot be undone.',
    })
  }

  return (
    <Dialog fullWidth maxWidth='sm' aria-labelledby={labelId} open={openDialog}>
      <FplDialogTitle id={labelId} onClose={onCloseDialog}>
        <Typography mb={0} paragraph variant='h4'>
          Select driver for allocation
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <Box pt='6px'>
          <Grid container spacing={1.5}>
            <Box hidden>
              <ControlledTextField control={control} name='bookingId' label='' />
            </Box>

            <Grid item xs={12}>
              <ControlledSelector
                control={control}
                name='driverId'
                options={availableDrivers}
                label='Driver'
                required
                error={!!errors.driverId}
                helperText={errors.driverId?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledSelector
                control={control}
                name='vehicleId'
                options={vehicles}
                label='Vehicle'
                required
                error={!!errors.vehicleId}
                helperText={errors.vehicleId?.message}
              />
            </Grid>
          </Grid>
        </Box>

        <LoadingBackdrop loading={loading || availableDriversLoading || vehiclesLoading} />
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={onCloseDialog}>
          Cancel
        </Button>

        <LoadingButton
          loading={loading || availableDriversLoading || vehiclesLoading}
          variant='contained'
          disabled={!isDirty || !isValid}
          onClick={handleSubmit(onSubmitSuccess)}>
          Allocate
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
